/**
 * Automatische Mass Messages: de logica van de Auto-weergave (11-10-2026, Jordy).
 *
 * Zonder React, zodat de tests hem los kunnen draaien (esbuild + node).
 *
 * "Gezien": een concept telt pas als gezien als het echt in beeld is geweest
 * (IntersectionObserver in MassaAuto.tsx), en alleen met de `view_hash` die
 * het op dat moment had. Verandert het concept daarna (na herladen een andere
 * hash), dan is het weer niet gezien. "Approve these N" stuurt alleen gezien
 * concepten mee, elk met die hash; de server keurt alleen goed wat nog precies
 * zo is.
 */

export interface AutoInfo {
  level?: string
  daypart?: string
  local?: string
  timezone?: string
  regenerated?: boolean
}

export interface AutoConcept {
  id: number
  platform: string
  model_slug: string
  model_name?: string | null
  content: string | null
  scheduled_at: string | null
  status: string
  view_hash: string
  auto?: AutoInfo
  only_test: boolean
  exclude_test?: boolean
  fc_delivery_mode?: string | null
  fc_all_followers?: boolean
  exclude_recent_minutes?: number
  exclude_timewasters?: boolean
  audience_include?: string | null
  exclude_spenders?: boolean
  exclude_new_followers?: boolean
  exclude_private_chats?: boolean
  online_only?: boolean
  translate_to?: string | null
  translation_enabled?: boolean
  problems?: string[]
  is_paid?: boolean
  price?: number
  has_media?: boolean
}

export interface Groep {
  sleutel: string
  model_slug: string
  model_name: string
  dag: string
  items: AutoConcept[]
}

/** Maximaal zoveel per bulk-goedkeuring (zelfde grens als de server). */
export const BULK_MAX = 50

/** De lokale dag van het concept (in de tijdzone van het model, uit de generator). */
export function lokaleDag(c: AutoConcept): string {
  return (c.auto?.local || c.scheduled_at || '').slice(0, 10)
}

/** De lokale tijd (HH:MM) van het concept, in de tijdzone van het model. */
export function lokaleTijd(c: AutoConcept): string {
  const lokaal = c.auto?.local
  return lokaal && lokaal.length >= 16 ? lokaal.slice(11, 16) : ''
}

/** Per model per dag, op tijd gesorteerd; groepen op dag en dan model. */
export function groepeer(items: AutoConcept[]): Groep[] {
  const map = new Map<string, Groep>()
  for (const c of items) {
    const dag = lokaleDag(c)
    const sleutel = `${c.model_slug}|${dag}`
    let g = map.get(sleutel)
    if (!g) {
      g = { sleutel, model_slug: c.model_slug, model_name: c.model_name || c.model_slug, dag, items: [] }
      map.set(sleutel, g)
    }
    g.items.push(c)
  }
  const groepen = [...map.values()]
  for (const g of groepen) g.items.sort((a, b) => String(a.scheduled_at).localeCompare(String(b.scheduled_at)))
  groepen.sort((a, b) => a.dag.localeCompare(b.dag) || a.model_name.localeCompare(b.model_name))
  return groepen
}

/** Gezien: per concept-id de hash die in beeld was. */
export type Gezien = Map<number, string>

export function markeerGezien(gezien: Gezien, c: AutoConcept): Gezien {
  if (gezien.get(c.id) === c.view_hash) return gezien
  const nieuw = new Map(gezien)
  nieuw.set(c.id, c.view_hash)
  return nieuw
}

export function isGezien(gezien: Gezien, c: AutoConcept): boolean {
  return gezien.get(c.id) === c.view_hash
}

/** De concepten van deze groep die goedgekeurd mogen worden: gezien met de huidige hash. */
export function goedTeKeuren(groep: Groep, gezien: Gezien): AutoConcept[] {
  return groep.items.filter((c) => c.status === 'draft' && isGezien(gezien, c)).slice(0, BULK_MAX)
}

/** Het verzoek voor POST drafts/approve-batch. */
export function bulkVerzoek(groep: Groep, gezien: Gezien) {
  return {
    scope: { model_slug: groep.model_slug, day: groep.dag },
    items: goedTeKeuren(groep, gezien).map((c) => ({ id: c.id, seen_hash: c.view_hash })),
  }
}

/** Het verzoek voor PATCH drafts/{id}: alle velden van het concept, alleen de tekst nieuw. */
export function bewerkVerzoek(c: AutoConcept, tekst: string, getalBevestigd: boolean) {
  return {
    model_slug: c.model_slug,
    platform: c.platform,
    content: tekst,
    scheduled_at: c.scheduled_at ?? '',
    only_test: c.only_test,
    exclude_test: c.exclude_test ?? true,
    fc_all_followers: c.fc_all_followers ?? false,
    fc_delivery_mode: c.fc_delivery_mode || 'native',
    exclude_recent_minutes: c.exclude_recent_minutes ?? 0,
    exclude_timewasters: c.exclude_timewasters ?? false,
    audience_include: c.audience_include || 'all',
    exclude_spenders: c.exclude_spenders ?? false,
    exclude_new_followers: c.exclude_new_followers ?? false,
    exclude_private_chats: c.exclude_private_chats ?? false,
    online_only: c.online_only ?? false,
    translate_to: c.translate_to ?? null,
    translation_enabled: c.translation_enabled ?? false,
    content_ids: [],
    is_paid: false,
    price: 0,
    number_confirmed: getalBevestigd,
  }
}

/** Zelfde soort controle als de server (src/services/mm_auto.py): staat er een getal of bedrag in? */
export function heeftGetalOfPrijs(tekst: string): boolean {
  return /\d/.test(tekst) || /[€$£]/.test(tekst) || /\b(ppv|credits?)\b/i.test(tekst)
}

/** 11-10-2026 (Jordy): in het volledige menu van een auto-concept alleen het eigen model
 *  (model en platform liggen vast; de bibliotheek volgt daarmee dit model). */
export function keuzesVoorConcept<K extends { slug: string; platform: string }>(keuzes: K[], c: AutoConcept): K[] {
  const platform = c.platform === 'f2f' ? 'f2f' : 'fancentro'
  return keuzes.filter((k) => k.slug === c.model_slug && k.platform === platform)
}

/** Het label bij een concept: tekst, gratis media of PPV met prijs (FanCentro credits, F2F euro). */
export function typeLabel(c: { is_paid?: boolean; price?: number; has_media?: boolean; platform: string }): string {
  if (c.is_paid) return c.platform === 'f2f' ? `PPV €${Number(c.price ?? 0).toFixed(2)}` : `PPV ${Number(c.price ?? 0)} credits`
  return c.has_media ? 'Free media' : 'Text'
}

export const NIVEAU_NAAM: Record<string, string> = { teasing: 'Teasing', flirty: 'Flirty', explicit: 'Explicit' }
export const WEEKDAG_NAAM = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
